import { useState } from "react";
import type { EventFieldView, EventSectionView, EventView } from "common";
import { EVENT_SECTION_FALLBACK_MESSAGE } from "common";
import EventFieldInput from "./event-field-input";
import EventRecordPicker from "./event-record-picker";
import {
  defaultsOf,
  linkSource,
  parentFilter,
  recordValuesFromRow,
  sectionMode,
  sharedSectionMode,
  willLinkOnSave,
  type EventFormAction,
  type EventFormState,
  type InstanceState,
} from "./event-form-state";

const STATUS_LABEL = {
  saved: "Saved",
  failed: "Failed",
  not_attempted: "Not attempted",
} as const;
const STATUS_CLASS = {
  saved: "bg-green-50 text-green-700",
  failed: "bg-red-50 text-red-700",
  not_attempted: "bg-gray-100 text-gray-600",
} as const;

/** Fields in display order; a multicolumn rule puts its fields in one row at its first member. */
export const layoutRows = (section: EventSectionView): EventFieldView[][] => {
  const visible = section.fields.filter(f => !f.hidden);
  const ruleOf = (f: EventFieldView) =>
    section.multicolumn.findIndex(
      rule => rule.includes(f.name) || rule.includes(f.id)
    );
  const rows: EventFieldView[][] = [];
  const placed = new Set<number>();
  for (const f of visible) {
    const r = ruleOf(f);
    if (r < 0) {
      rows.push([f]);
    } else if (!placed.has(r)) {
      placed.add(r);
      rows.push(visible.filter(g => ruleOf(g) === r));
    }
  }
  return rows;
};

const GRID = ["", "grid-cols-1", "grid-cols-2", "grid-cols-3", "grid-cols-4"];

/** Whether an instance holds edits made after its record loaded. */
const editedSinceLoad = (inst: InstanceState): boolean =>
  Object.keys(inst.loaded).length > 0 &&
  Object.keys(inst.loaded).some(
    name => inst.values[name] !== inst.loaded[name]
  );

interface Props {
  view: EventView;
  state: EventFormState;
  section: EventSectionView;
  instance: number;
  dispatch: (action: EventFormAction) => void;
  disabled?: boolean;
}

const EventSectionCard = ({
  view,
  state,
  section,
  instance,
  dispatch,
  disabled,
}: Props) => {
  const inst = state.sections[section.key]?.[instance];
  const [open, setOpen] = useState(
    !(section.collapsible && section.collapseOnLoad)
  );
  if (!inst) return null;

  const mode = section.sharesRecordWith
    ? sharedSectionMode(view, state, section, instance)
    : sectionMode(section, inst);
  const readOnly = disabled || mode === "read_only";
  const errorField =
    inst.result?.error?.field ?? inst.clientError?.field ?? null;
  const errorMessage =
    inst.result?.status === "failed"
      ? inst.result.error?.message || EVENT_SECTION_FALLBACK_MESSAGE
      : (inst.clientError?.message ?? null);
  const title = `${section.collectionLabel}${instance > 0 ? ` (${instance + 1})` : ""}`;
  const idBase = `event-${section.key}-${instance}`.replace(
    /[^A-Za-z0-9_-]/g,
    "_"
  );

  // Sections that save into this section's record show that record at the same instance.
  const sharedSections = view.sections.filter(
    s => s.sharesRecordWith === section.key
  );

  const clear = () => {
    dispatch({
      type: "clearRecord",
      key: section.key,
      instance,
      values: defaultsOf(section),
    });
    for (const s of sharedSections) {
      if (!state.sections[s.key]?.[instance]) continue;
      dispatch({
        type: "clearRecord",
        key: s.key,
        instance,
        values: defaultsOf(s),
      });
    }
  };

  // An id this form saved is never dropped: a reload that misses it must not turn the next
  // save into a second insert.
  const onUnreadable = () => {
    if (inst.result?.status === "saved") return;
    clear();
  };

  const onPick = (
    recordId: string | null,
    row: Record<string, unknown> | null
  ) => {
    if (!recordId) return clear();
    const values = row ? recordValuesFromRow(section, row) : {};
    // The picker re-reports a loaded record whenever it refetches. Keep unsaved edits then.
    const sameRecord = inst.recordId === recordId;
    if (sameRecord && editedSinceLoad(inst)) return;
    dispatch({
      type: "pickRecord",
      key: section.key,
      instance,
      recordId,
      values,
    });
    // The reducer keeps the user's input in a touched shared instance.
    for (const s of sharedSections) {
      if (!state.sections[s.key]?.[instance]) continue;
      dispatch({
        type: "loadShared",
        key: s.key,
        instance,
        loaded: row ? recordValuesFromRow(s, row) : {},
        defaults: defaultsOf(s),
      });
    }
  };

  const header = (
    <div className="flex items-center justify-between">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {inst.result && (
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[inst.result.status]}`}>
          {STATUS_LABEL[inst.result.status]}
        </span>
      )}
    </div>
  );

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      {section.collapsible ? (
        <button
          type="button"
          className="w-full text-left"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}>
          {header}
        </button>
      ) : (
        header
      )}
      {open && (
        <div className="mt-3 flex flex-col gap-3">
          {section.sharesRecordWith ? (
            <p className="text-sm text-gray-500">
              Saved into the same record as the section above
            </p>
          ) : (
            // A section that cannot update still shows a record it was given: the picker is
            // its only loader, so it renders locked and loads that row by id (#1170).
            (section.update || inst.recordId) && (
              <EventRecordPicker
                canvasId={view.canvasId}
                collectionName={section.collectionName}
                value={inst.recordId}
                inputId={`${idBase}-record`}
                disabled={disabled || !section.update}
                placeholder={section.insert ? "New record" : "Choose a record"}
                parent={parentFilter(view, state, section, instance)}
                onUnreadable={onUnreadable}
                onChange={onPick}
              />
            )
          )}
          {layoutRows(section).map(row => (
            <div
              key={row.map(f => f.id).join("|")}
              className={`grid gap-3 ${GRID[Math.min(row.length, 4)]}`}>
              {row.map(f => {
                const source = willLinkOnSave(view, state, section, instance, f)
                  ? linkSource(view, state, section, f)
                  : null;
                return (
                  <EventFieldInput
                    key={f.id}
                    canvasId={view.canvasId}
                    field={f}
                    value={inst.values[f.name] ?? ""}
                    inputId={`${idBase}-${f.id}`}
                    disabled={readOnly}
                    invalid={errorField === f.name}
                    linkHint={
                      source
                        ? `Linked to the ${source.collectionLabel} record on save`
                        : null
                    }
                    onChange={value =>
                      dispatch({
                        type: "setValue",
                        key: section.key,
                        instance,
                        field: f.name,
                        value,
                      })
                    }
                  />
                );
              })}
            </div>
          ))}
          {errorMessage && (
            <p role="alert" className="text-sm text-red-600">
              {errorMessage}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default EventSectionCard;
